import {getAllSignDocTypes} from '../Components/SignDoc/SignDocLib';

/**
 * Utility functions for manipulating filter models in DataGrid components.
 * Centralizes common filter operations to avoid code duplication.
 * 
 * @module filterUtils
 */

/**
 * Creates a deep copy of filter model to prevent mutation of original object.
 * Uses JSON serialization for deep cloning.
 * 
 * @param {object} filterModel - Original filter model from DataGrid containing filterValues, filterData, etc.
 * @returns {object} Deep copy of filter model that can be safely mutated.
 * 
 * @example
 * const originalFilter = {filterValues: {organizationIds: ['org1']}};
 * const copy = cloneFilterModel(originalFilter);
 * copy.filterValues.organizationIds = ['org2']; // Original unchanged
 */
export const cloneFilterModel = (filterModel) => {
    return JSON.parse(JSON.stringify(filterModel));
};

/**
 * Adds all available SignDoc types to filter model.
 * Used when exporting reports that should include all content types.
 * Does not mutate the original filter model.
 * 
 * @param {object} filterModel - Filter model to modify.
 * @returns {object} New filter model with all SignDoc types added to filterValues.types.
 * 
 * @example
 * const filter = {filterValues: {organizationIds: ['org1']}};
 * const withTypes = addAllSignDocTypes(filter);
 * // Result: {filterValues: {organizationIds: ['org1'], types: ['REGULAR_COURSE', 'EXAM', ...]}}
 */
export const addAllSignDocTypes = (filterModel) => {
    const filterCopy = cloneFilterModel(filterModel);
    filterCopy.filterValues.types = getAllSignDocTypes();
    return filterCopy;
};

/**
 * Sets organization IDs to 'all' in filter model.
 * Used when exporting reports for all organizations admin has access to.
 * Does not mutate the original filter model.
 * 
 * @param {object} filterModel - Filter model to modify.
 * @returns {object} New filter model with organizationIds set to ['all'].
 * 
 * @example
 * const filter = {filterValues: {organizationIds: ['org1', 'org2']}};
 * const forAll = setAllOrganizations(filter);
 * // Result: {filterValues: {organizationIds: ['all']}}
 */
export const setAllOrganizations = (filterModel) => {
    const filterCopy = cloneFilterModel(filterModel);
    filterCopy.filterValues.organizationIds = ['all'];
    return filterCopy;
};

/**
 * Convenience function that applies both addAllSignDocTypes and setAllOrganizations.
 * Used for full organization reports that include all content types.
 * Does not mutate the original filter model.
 * 
 * @param {object} filterModel - Filter model to modify.
 * @returns {object} New filter model with organizationIds set to ['all'] and all SignDoc types.
 * 
 * @example
 * const filter = {filterValues: {organizationIds: ['org1']}};
 * const fullReport = prepareFullReportFilter(filter);
 * // Result: {filterValues: {organizationIds: ['all'], types: ['REGULAR_COURSE', ...]}}
 */
export const prepareFullReportFilter = (filterModel) => {
    return addAllSignDocTypes(setAllOrganizations(filterModel));
};

